import type { ComponentProps } from 'react';
import React from 'react';
import type {
  ImageSourcePropType,
  PressableProps,
  ViewProps,
} from 'react-native';
import type ReactNative from 'react-native';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { COLORS, SPACING, Z_INDEX } from '@garnish/constants';
import { useResponsive } from '../../hooks';
import { IconLink } from '../Icon';
import { Image } from '../Image';
import { BodyText, DisplayText, LabelText } from '../Text';
import type { PaletteProps } from './Modal.types';
import { useModalBottomSpacing } from './hooks';

export const ModalRow = (props: ReactNative.ViewProps) => {
  const { children, style, ...restProps } = props;
  const { match } = useResponsive();
  const responsiveStyles = match([
    styles.bodyElementMobile,
    styles.bodyElementTabletUp,
  ]);

  return (
    <View style={[responsiveStyles, style]} {...restProps}>
      {children}
    </View>
  );
};

export const ModalCloseBtn = (
  props: Omit<ComponentProps<typeof IconLink>, 'name' | 'palette'> &
    PaletteProps,
) => {
  const { palette, ...rest } = props;
  const { match } = useResponsive();
  const responsiveStyles = match([
    styles.closeBtnWrapperMobile,
    styles.closeBtnWrapperTabletUp,
  ]);

  const paletteSpecificColor = palette ? iconColors[palette] : undefined;

  return (
    <ModalRow
      style={[styles.closeBtnWrapper, responsiveStyles, props.style]}
      testID="sg-modal-close-btn"
    >
      <IconLink name="IconClose" color={paletteSpecificColor} {...rest} />
    </ModalRow>
  );
};

export const ModalHeader = ({
  children,
  onBack,
  onClose,
}: {
  readonly children?: React.ReactNode;
  readonly onBack?: () => void;
  readonly onClose: () => void;
}) => {
  const { match } = useResponsive();
  const headerMargin = match([
    styles.headerMarginMobile,
    styles.headerMarginTabletUp,
  ]);
  const headerMarginRight = match([
    styles.headerMarginRightMobile,
    styles.headerMarginRightTabletUp,
  ]);
  const headerMarginLeft = match([
    styles.headerMarginLeftMobile,
    styles.headerMarginLeftTabletUp,
  ]);

  return (
    <View style={[styles.header, headerMargin]}>
      {onBack ? (
        <IconLink
          style={headerMarginRight}
          name="IconArrowLeft"
          onPress={onBack}
          accessibilityHint=""
          accessibilityLabel="back"
          accessibilityRole="imagebutton"
        />
      ) : null}
      {children}
      <IconLink
        style={headerMarginLeft}
        name="IconClose"
        onPress={onClose}
        accessibilityHint=""
        accessibilityLabel="close"
        accessibilityRole="imagebutton"
      />
    </View>
  );
};

export const ModalFloatingCloseBtn = (props: ModalFloatingCloseBtnProps) => {
  const { style, palette, onPress } = props;
  const buttonStyles = [styles.closeBtnFloating, style];
  const paletteSpecificColor = palette ? iconColors[palette] : undefined;

  return (
    <IconLink
      testID="sg-modal-close-btn"
      style={buttonStyles}
      color={paletteSpecificColor}
      name="IconClose"
      onPress={onPress}
      accessibilityHint=""
      accessibilityLabel="close"
      accessibilityRole="imagebutton"
    />
  );
};

export const ModalHeadline = (
  props: ComponentProps<typeof DisplayText> & PaletteProps,
) => {
  const { children, palette, style, ...restProps } = props;
  const { match } = useResponsive();

  const paletteSpecificTextStyles = palette
    ? paletteHeadingTextStyles[palette]
    : undefined;

  return (
    <ModalRow>
      <DisplayText
        size={match([4, 3])}
        style={[styles.textGap, style, paletteSpecificTextStyles]}
        {...restProps}
      >
        {children}
      </DisplayText>
    </ModalRow>
  );
};

export const ModalSubhead = (
  props: ComponentProps<typeof LabelText> & PaletteProps,
) => {
  const { children, palette, ...restProps } = props;

  const paletteSpecificTextStyles = palette
    ? paletteSubheadTextStyles[palette]
    : undefined;

  return (
    <ModalRow>
      <LabelText
        size={2}
        style={[styles.subhead, styles.textGap, paletteSpecificTextStyles]}
        {...restProps}
      >
        {children}
      </LabelText>
    </ModalRow>
  );
};

export const ModalBodyText = (
  props: ComponentProps<typeof BodyText> & PaletteProps,
) => {
  const { children, ...restProps } = props;
  const { match } = useResponsive();

  return (
    <ModalRow>
      <BodyText size={match([4, 3])} style={styles.bodyText} {...restProps}>
        {children}
      </BodyText>
    </ModalRow>
  );
};

export const ModalImage = (props: ModalImageProps) => {
  const { imageUrl, style, ...rest } = props;

  return (
    <Image
      source={imageUrl}
      resizeMode="contain"
      accessibilityIgnoresInvertColors
      style={[styles.illustration, style]}
      {...rest}
    />
  );
};

export const ModalFooter = (props: ModalFooterProps) => {
  const {
    children,
    style,
    withoutTopBorder,
    withoutBottomPadding,
    ...restProps
  } = props;
  const { match } = useResponsive();

  const modalBottomSpacing = useModalBottomSpacing();
  const modalFooterBottomSpacingStyle = useStyle(
    () => ({ paddingBottom: modalBottomSpacing }),
    [modalBottomSpacing],
  );

  const responsiveStyles = match([styles.footerMobile, styles.footerTabletUp]);

  return (
    <ModalRow
      style={[
        responsiveStyles,
        withoutTopBorder
          ? styles.footerWithoutBorder
          : styles.footerWithTopBorder,
        withoutBottomPadding ? undefined : modalFooterBottomSpacingStyle,
        style,
      ]}
      {...restProps}
    >
      {children}
    </ModalRow>
  );
};

//
// ─── TYPES ──────────────────────────────────────────────────────────────────────
//

type ModalImageProps = {
  readonly imageUrl?: ImageSourcePropType;
} & React.ComponentProps<typeof Image>;

type ModalFooterProps = ReactNative.ViewProps & {
  readonly withoutTopBorder?: boolean;
  readonly withoutBottomPadding?: boolean;
};

type ModalFloatingCloseBtnProps = {
  readonly style?: ViewProps['style'];
  readonly onPress: PressableProps['onPress'];
} & PaletteProps;

//
// ─── STYLES ──────────────────────────────────────────────────────────────────────
//

const styles = StyleSheet.create({
  closeBtnWrapper: {
    alignItems: 'flex-end',
    paddingBottom: SPACING.medium,
  },
  closeBtnWrapperMobile: {
    paddingTop: SPACING.medium,
  },
  closeBtnWrapperTabletUp: {
    paddingTop: SPACING.large,
  },
  closeBtnFloating: {
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: Z_INDEX.modal,
    margin: SPACING.medium,
  },

  // ========================

  textGap: {
    marginBottom: SPACING.small,
  },

  // ========================

  headlineSecondary: {
    color: COLORS.WHITE,
  },

  // ========================

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerMarginMobile: {
    margin: SPACING.medium,
  },
  headerMarginTabletUp: {
    margin: SPACING.large,
  },
  headerMarginRightMobile: {
    marginRight: SPACING.medium,
  },
  headerMarginRightTabletUp: {
    marginRight: SPACING.large,
  },
  headerMarginLeftMobile: {
    marginLeft: SPACING.medium,
  },
  headerMarginLeftTabletUp: {
    marginLeft: SPACING.large,
  },

  // ========================

  subhead: {
    textTransform: 'uppercase',
  },
  subheadSecondary: {
    color: COLORS.WHITE,
  },

  // ========================

  bodyElementMobile: {
    paddingHorizontal: SPACING.medium,
  },
  bodyElementTabletUp: {
    paddingHorizontal: SPACING.large,
  },

  // ========================

  bodyText: {
    paddingBottom: SPACING.medium,
  },

  // ========================

  illustration: {
    width: 200,
    height: 200,
    alignSelf: 'center',
  },

  // ========================

  footerMobile: {
    paddingTop: SPACING.medium,
  },
  footerTabletUp: {
    paddingTop: SPACING.large,
  },
  footerWithTopBorder: {
    borderTopWidth: 1,
    borderTopColor: COLORS.LIGHT,
  },
  footerWithoutBorder: {
    paddingTop: 0,
  },
});

//
// ─── PALETTE STYLES ──────────────────────────────────────────────────────────────────────
//

const iconColors = {
  primary: undefined,
  secondary: COLORS.WHITE,
};

const paletteHeadingTextStyles = {
  primary: undefined,
  secondary: styles.headlineSecondary,
};

const paletteSubheadTextStyles = {
  primary: undefined,
  secondary: styles.subheadSecondary,
};
